/**
 * Session pane for the right sidebar - shows active chat session
 */

import {
  useFlowSessionById,
  useUpdateFlowSessionReadStatus,
} from "@/api/flow_sessions";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MessageInputControlSet } from "@/constants/message-input";
import { ROUTES } from "@/constants/routes";
import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { route } from "@/utils/route";
import {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  PaneLayoutHeaderDescription,
  PaneLayoutHeaderIcon,
  PaneLayoutHeaderTitle,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { Maximize2, MessagesSquare, Minimize2, X } from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SessionChatArea from "../../components/SessionChatArea";

const SessionPaneInner: React.FC<{
  flowSession: any;
}> = ({ flowSession }) => {
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();
  const lg = useBreakpoint("lg");
  const paneContext = usePaneRouterContextSafe();
  const { mutate: updateFlowSessionReadStatus } =
    useUpdateFlowSessionReadStatus();

  useEffect(() => {
    if (flowSession?.read_status === "unread" && flowSession?.id) {
      updateFlowSessionReadStatus({
        flowSessionId: flowSession.id,
        readStatus: "read",
      });
    }
  }, [flowSession?.read_status, flowSession?.id, updateFlowSessionReadStatus]);

  const createdAt = flowSession?.created_at
    ? formatDistanceToNow(new Date(flowSession.created_at), { addSuffix: true })
    : undefined;

  const displayTitle = flowSession?.name || "New Chat";

  const getSessionIcon = () => <MessagesSquare />;
  const getIconColor = () => "blue" as const;

  return (
    <PaneLayout>
      <PaneLayoutHeader>
        <PaneLayoutHeaderIcon color={getIconColor()}>
          {getSessionIcon()}
        </PaneLayoutHeaderIcon>
        <PaneLayoutHeaderTitle>{displayTitle}</PaneLayoutHeaderTitle>
        <PaneLayoutHeaderDescription>
          <div className="flex items-center gap-2">
            <>
              <span>Chat</span>
              {createdAt && <span>• {createdAt}</span>}
            </>
          </div>
        </PaneLayoutHeaderDescription>
        <PaneLayoutHeaderActions>
          <TooltipProvider>
            {/* Expand/Minimize */}
            {lg && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    onClick={() => {
                      if (paneContext) {
                        navigate(
                          route(ROUTES.CHAT.DETAIL, {
                            flowSessionId: flowSession.id,
                          }),
                        );
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    {paneContext ? <Maximize2 /> : <Minimize2 />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {paneContext ? "Open in full page" : "Close full page"}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Close */}
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    onClick={() => appNavigate({ pane: "rightPane", to: null })}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent
        noScroll
        className="relative overflow-hidden pb-0 pt-0"
      >
        <div className="max-h-full h-full flex flex-col w-full scrollbar-hide">
          <SessionProvider initialSessionId={flowSession.id}>
            <SessionChatArea
              surface="sidechat"
              location="sidebar"
              controlSet={MessageInputControlSet.SIDECHAT}
            />
          </SessionProvider>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
};

export const SessionPane = () => {
  const sessionId = usePaneParams(ROUTES.SESSIONS.SESSION)?.sessionId;

  const { data: flowSession, isLoading: isFlowSessionsLoading } =
    useFlowSessionById(sessionId || "", {
      enabled: !!sessionId,
    });

  if (isFlowSessionsLoading) {
    return null;
  }

  if (!sessionId) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-2">Session</h2>
        <p className="text-muted-foreground text-sm">No session ID provided.</p>
      </div>
    );
  }

  if (!flowSession) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-2">Session</h2>
        <p className="text-muted-foreground text-sm">Session not found.</p>
      </div>
    );
  }

  return (
    <DropZoneProvider>
      <MessageInputProvider>
        <SessionPaneInner flowSession={flowSession} />
      </MessageInputProvider>
    </DropZoneProvider>
  );
};
